<template>
  <div class="users-page">
    <div class="page-container">
      <div class="page-header">
        <h3 class="page-title">{{ t('Usuários') }}</h3>
        <div class="page-actions">
          <Button
            :label="t('Novo Usuário')"
            icon="pi pi-plus"
            data-testid="users-create-button"
            @click="handleCreate"
          />
        </div>
      </div>
      <div class="page-body">
        <UsersTable
          @edit="handleEdit"
          @disable="handleDisable"
          @enable="handleEnable"
          @change-password="handleChangePassword"
          @delete="handleDelete"
        />
      </div>
    </div>

<UserOnboardingWizard
  v-if="showOnboardingWizard"
  :show="showOnboardingWizard"
  @close="handleCloseOnboardingWizard"
  @complete="handleOnboardingComplete"
/>

<!-- UserWizard is centralized in ModalManager.vue and opened via eventBus -->
<!-- ChangePasswordModal is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import UsersTable from './UsersTable.vue';
// UserWizard is centralized in ModalManager.vue and opened via eventBus
// ChangePasswordModal is centralized in ModalManager.vue and opened via eventBus
import UserOnboardingWizard from './onboarding/UserOnboardingWizard.vue';
import { useUsers } from '@/composables/useUsers';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

interface UserRow {
  sub?: number | string;
  username?: string;
  name?: string;
  email?: string;
  enabled?: boolean | 0 | 1;
  groups?: string[];
  franquias?: string[];
  [key: string]: unknown;
}

const { t } = useI18n();

// Composables
const { enableUser, disableUser, deleteUser } = useUsers();
const { confirmAction } = useConfirmAction();

// Data
const showOnboardingWizard = ref<boolean>(false);

// Methods
const handleCreate = (): void => {
  log('UsersPage', 'handleCreate', 'Opening onboarding wizard');
  showOnboardingWizard.value = true;
};

const handleEdit = (user: UserRow): void => {
  log('UsersPage', 'handleEdit', 'Opening edit modal via eventBus', user);
  eventBus.emit('modal.user-wizard.open', { mode: 'edit', user: user });
};

const handleDisable = (user: UserRow): void => {
  log('UsersPage', 'handleDisable', 'Disable requested', user);

  confirmAction({
    message: t('Tem certeza que deseja desativar este usuário?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await disableUser(user.sub as string);
        eventBus.emit('globalMessage.success', t('Usuário desativado com sucesso'));
        eventBus.emit('users.refresh');
      } catch (error: unknown) {
        log('UsersPage', 'handleDisable', 'Error disabling user', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', t('Erro ao desativar usuário') + ': ' + message);
      }
    }
  });
};

const handleEnable = (user: UserRow): void => {
  log('UsersPage', 'handleEnable', 'Enable requested', user);

  confirmAction({
    message: t('Tem certeza que deseja ativar este usuário?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await enableUser(user.username as string);
        eventBus.emit('globalMessage.success', t('Usuário ativado com sucesso'));
        eventBus.emit('users.refresh');
      } catch (error: unknown) {
        log('UsersPage', 'handleEnable', 'Error enabling user', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', t('Erro ao ativar usuário') + ': ' + message);
      }
    }
  });
};

const handleChangePassword = (user: UserRow): void => {
  log('UsersPage', 'handleChangePassword', 'Opening password modal via eventBus', user);
  eventBus.emit('modal.changepassword.open', { user });
};

const handleDelete = (user: UserRow): void => {
  log('UsersPage', 'handleDelete', 'Delete requested', user);

  confirmAction({
    message: t('Tem certeza que deseja excluir permanentemente este usuário?'),
    header: t('Confirmação'),
    acceptLabel: t('Excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        await deleteUser(user.sub as string);
        eventBus.emit('globalMessage.success', t('Usuário excluído com sucesso'));
        eventBus.emit('users.refresh');
      } catch (error: unknown) {
        log('UsersPage', 'handleDelete', 'Error deleting user', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir usuário') + ': ' + message);
      }
    }
  });
};

const handleCloseOnboardingWizard = (): void => {
  log('UsersPage', 'handleCloseOnboardingWizard', 'Closing onboarding wizard');
  showOnboardingWizard.value = false;
};

const handleOnboardingComplete = (createdUser: UserRow): void => {
  log('UsersPage', 'handleOnboardingComplete', 'Onboarding wizard completed', createdUser);
  showOnboardingWizard.value = false;
  eventBus.emit('globalMessage.success', t('Usuário configurado com sucesso'));
  eventBus.emit('users.refresh');
};

// Lifecycle
onMounted(() => {
  log('UsersPage', 'onMounted', 'Page mounted');
});
</script>

<style scoped>
.users-page {
  padding: 0;
}

.page-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.page-header {
  padding: 15px 20px;
  border-bottom: 1px solid #f4f4f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.page-body {
  padding: 20px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-actions {
    width: 100%;
  }

  .page-actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
